/**
 * TalkroutePhoneNumber.js
 * Talkroute
 *
 * Created by Vel Genov on 1/28/19
 * Copyright (c) 2019 Talkroute. All right reserved.
 */

import { parsePhoneNumberFromString } from "libphonenumber-js";
import validateArray from "../../helpers/arrayValidator";

export const COUNTRY_CODE = "1";

/**
 * A class to represent a Talkroute phone number. Each phone number
 *
 */
export class TalkroutePhoneNumber {
  public id: number;
  public phoneNumber: string;
  public voiceEnabled: boolean;
  public textMessagingEnabled: boolean;
  public textMessagingActive: boolean;
  public textMessagingAllowed: boolean;
  public callRecordingAllowed: boolean;
  public editable: boolean;
  constructor(opts) {
    this.id = opts.id;
    this.phoneNumber = opts.phoneNumber;
    this.voiceEnabled = opts.voiceEnabled;
    this.textMessagingEnabled = opts.textMessagingEnabled;
    this.textMessagingActive = opts.textMessagingActive;
    this.textMessagingAllowed = opts.textMessagingAllowed;
    this.callRecordingAllowed = opts.callRecordingAllowed;
    this.editable = opts.editable;
  }

  /**
   * Formats a passed in phone number. If not a phone number, then returns the passed in value
   *
   * @returns Either a String of the passed in phone number, or a passed in value, if
   * the passed in value can't be parsed as a phone number
   */
  formatPhoneNumber = (phoneNumber) => {
    try {
      const updatedPhoneNumber =
        phoneNumber.charAt(0) === COUNTRY_CODE
          ? phoneNumber
          : `${COUNTRY_CODE} ${phoneNumber}`;

      const parsedPhoneNumber = parsePhoneNumberFromString(
        `+${updatedPhoneNumber}`
      );
      return `${COUNTRY_CODE} ${parsedPhoneNumber?.formatNational()}`;
    } catch (error) {
    }
    return phoneNumber;
  };

  /**
   * Get a formatted version of the phone number
   *
   * @returns {*} a formatted version of the phone number. If it can't be formatted,
   * return an unformatted one
   */
  getPhoneNumberFormatted = () => this.formatPhoneNumber(this.phoneNumber);

  /**
   * Getter for the phone number
   *
   * @return {*} the phone number string unformatted
   */
  getPhoneNumber = () => this.phoneNumber;

  /**
   * A getter for voice_enabled
   *
   * @returns {*} true if voice is enabled for the phone number, false otherwise
   */
  getVoiceEnabled = () => this.voiceEnabled;

  /**
   * A getter for text_messaging_enabled
   *
   * @returns {*} true if text messaging is enabled for the phone number, false otherwise
   */
  getTextMessagingEnabled = () => this.textMessagingEnabled;

  /**
   * A getter for text_messaging_allowed
   *
   * @returns {*} true if text messaging is allowed for the phone number, false otherwise
   */
  getTextMessagingAllowed = () => this.textMessagingAllowed;

    /**
   * A getter for editable
   *
   * @returns {*} true if the phone number is editable, false otherwise
   */
    getEditable = () => this.editable;
}

/**
 * Return only entries that have textMessagingEnabled
 *
 * @param {*} phoneNumbers An array of TalkroutePhoneNumber to filter
 */
export const getMessagingPhoneNumbers = (phoneNumbers) => {
  if (validateArray(phoneNumbers)) {
    const messagingNumbers = phoneNumbers.filter(
      (phoneRecord) => phoneRecord.getTextMessagingEnabled()
    );
    return messagingNumbers;
  }
  return phoneNumbers;
};

  /**
 *  A static method to find a virtual number with the given phone number and return its editable property. Return false if not found
 *
 * @param {*} phoneNumber the virtual number we are looking for
 * @param {*} phoneNumbers An array of TalkroutePhoneNumber to filter
 */

export const getEditableForNumber = (currentNumber, phoneNumbers) => {

  if (Array.isArray(phoneNumbers) && phoneNumbers.length > 0) {
    // Returns the element if it finds it. Unidentified otherwise
    const virtualNumber = phoneNumbers.find((entry) => {
      const { phoneNumber } = entry;
      return currentNumber == phoneNumber;
    });

    if (virtualNumber) {
      return virtualNumber.editable;
    }
  }
  return false;
};

  /**
 *  A static method to find a virtual number with the given phone number and return its text message allowed property. Return false if not found
 *
 * @param {*} phoneNumber the virtual number we are looking for
 * @param {*} phoneNumbers An array of TalkroutePhoneNumber to filter
 */

  export const getTextMessageAllowedForNumber = (currentNumber, phoneNumbers) => {

    if (Array.isArray(phoneNumbers) && phoneNumbers.length > 0) {
      // Returns the element if it finds it. Unidentified otherwise
      const virtualNumber = phoneNumbers.find((entry) => {
        const { phoneNumber } = entry;
        return currentNumber == phoneNumber;
      });
  
      if (virtualNumber) {
        return virtualNumber.textMessagingAllowed;
      }
    }
    return false;
  };

    /**
 *  A static method to find a virtual number with the given phone number and return its text message enabled property. Return false if not found
 *
 * @param {*} phoneNumber the virtual number we are looking for
 * @param {*} phoneNumbers An array of TalkroutePhoneNumber to filter
 */

    export const getTextMessageEnabledForNumber = (currentNumber, phoneNumbers) => {

      if (Array.isArray(phoneNumbers) && phoneNumbers.length > 0) {
        // Returns the element if it finds it. Unidentified otherwise
        const virtualNumber = phoneNumbers.find((entry) => {
          const { phoneNumber } = entry;
          return currentNumber == phoneNumber;
        });
    
        if (virtualNumber) {
          return virtualNumber.textMessagingEnabled;
        }
      }
      return false;
    };

  /**
 *  A static method to find a virtual number with the given phone number and return its call recording property. Return false if not found
 *
 * @param {*} phoneNumber the virtual number we are looking for
 * @param {*} phoneNumbers An array of TalkroutePhoneNumber to filter
 */

  export const getCallRecordingForNumber = (currentNumber, phoneNumbers) => {

    if (Array.isArray(phoneNumbers) && phoneNumbers.length > 0) {
      // Returns the element if it finds it. Unidentified otherwise
      const virtualNumber = phoneNumbers.find((entry) => {
        const { phoneNumber } = entry;
        return currentNumber == phoneNumber;
      });
  
      if (virtualNumber) {
        return virtualNumber.callRecordingAllowed;
      }
    }
    return false;
  };

/**
 * Parses the incoming JSON phone numbers. Creates a TalkroutePhoneNumber for each line item
 *
 * @param {*} phoneNUmberData an array containig phone number data.
 */
export const phoneNumberJSONParser = (phoneNumberData) => {
  // If valid data is passed, proceed. Otherwise don't return anything
  if (Array.isArray(phoneNumberData) && phoneNumberData.length > 0) {
    // Process each entry
    const parsedData = phoneNumberData.map((entry) => {
      const {
        id,
        number: phoneNumber,
        voice_enabled: voiceEnabled,
        text_messaging_enabled: textMessagingEnabled,
        text_messaging_status: textMessagingStatus,
        text_messaging_allowed: textMessagingAllowed,
        call_recording_allowed: callRecordingAllowed,
        editable,
      } = entry;

      const textMessagingActive = textMessagingStatus;

      const phoneNumberEntry = new TalkroutePhoneNumber({
        id,
        phoneNumber,
        voiceEnabled,
        textMessagingEnabled,
        textMessagingActive,
        textMessagingAllowed,
        callRecordingAllowed,
        editable,
      });

      return phoneNumberEntry;
    });

    return parsedData;
  }
};

/**
 * Parses the incoming generic objects into TalkroutePhoneNumber objects.
 * Checks for empty objects passed in
 *
 * @param {*} phoneNumberData an array containig phone numbers.
 */
export const talkroutePhoneNumberRehydrator = (phoneNumberData) => {
  // If valid data is passed, proceed. Otherwise don't return anything
  if (Array.isArray(phoneNumberData) && phoneNumberData.length > 0) {
    // Process each entry
    const parsedData = phoneNumberData.map((entry) =>
      rehydrateTalkroutePhoneNumber(entry)
    );
    return parsedData;
  }
  return [];
};

/**
 * Parses the incoming single generic object into TalkroutePhoneNumber objects.
 * Checks for an empty object being passed in.
 *
 * @param {*} phoneNumberData a single generic object containing phone numbers.
 */
export const rehydrateTalkroutePhoneNumber = (phoneNumberData) => {
  if (phoneNumberData) {
    const {
      id,
      phoneNumber,
      textMessagingEnabled,
      voiceEnabled,
    } = phoneNumberData;

    const phoneNumberEntry = new TalkroutePhoneNumber({
      id,
      phoneNumber,
      textMessagingEnabled,
      voiceEnabled,
    });

    return phoneNumberEntry;
  }
  return phoneNumberData;
};
